/**
 * JML pour Json Markup Langage : traduction d'un contenu XML en JSON.
 * Permet des d'optimiser la transmission de contenus XML dans des messages Json.
 *
 * Jml format :
 * <pre>
 * [
 *   {'':"!DOCTYPE", '=':"html"},
 *   {'':"!", '=':"Commentaire avant le root du doc"},
 *   {'':"html", attr1:"value1", xmlns:"http://www.w3.org/1999/xhtml"},
 *   [
 *     "noeud texte",
 *     {'':"br"},
 *     {'':"p", att:"v", 'xml:space':"preserve"},
 *     [
 *       "text avant span",
 *       {'':"span"},
 *       ["text dans span"],
 *       "text après span",
 *       {'':"a:x" 'xmlns:a':"ns"},
 *     ],
 *     {'':"!", '=':"mon commentaire"},
 *     {'':"?piName", '=':"content du PI"},
 *   ]
 * ]
 * </pre>
 *
 * Note d'impl : Jml n'est qu'un tableau Js standard, pas une "class Jml extends Array" pour éviter une duplication du tableau au parsing json.
 */

import {DOM} from "./dom"
import {IXAddr, XA} from "./xAddr";
import {LANG} from "lib/commons/lang";

/**
 * Représente un noeud DOM de type Element (avec ses attributs), Comment ou PI (avec ses datas dans la property '=').
 */
interface IJmlObj {
	[index: string]: string;
}

/**
 * Représente un noeud DOM de type Element, Comment, PI ou ou un noeud Text.
 */
type IJmlNode = IJmlObj | string;

/**
 * Représente un noeud DOM de type Document, DocumentFragment, et une séquence de noeuds fils.
 * C'est un tableau de [ IJmlNode | IJmlSet ? ]*
 */
interface IJmlSet extends Array<IJmlNode | IJmlSet> {
}

/**
 * Représente une séquence partielle de noeuds. Un number représente le poids approximatif d'un noeud
 * qui existe mais non présents dans ce IJmlSubSet.
 * Exemple: [{'':'div'}[1034, 5032]] = le div contient 2 noeuds fils mais ne sont pas présents ici.
 * Exemple: [1250, 532, "text1", {'':'img'}, "text2", 856 ] = 2 noeuds précèdent la séquence text+img+text suivi de 1 autre noeud.
 * Calcul du poids approximatif par un parcours du noeud : cf JML.computeWeightNode().
 */
interface IJmlSubSet extends Array<IJmlNode | IJmlSubSet | number> {
}

export namespace JML {

	export function newJml(): IJmlSet {
		return [];
	}

	// export function newJmlNode(nodeType: number, name: string): IJmlNode {
	// 	switch (nodeType) {
	// 	case Node.ELEMENT_NODE :
	// 		return {'': name};
	// 	case Node.COMMENT_NODE :
	// 		return {'': "!"};
	// 	case Node.TEXT_NODE :
	// 		return "";
	// 	case Node.PROCESSING_INSTRUCTION_NODE :
	// 		return {'': "?" + name};
	// 	}
	// 	return null;
	// }

	/** @see JmlBuilder#setStartCtx */
	export function newBd(startContex?: IJmlSet | IJmlNode): JmlBuilder {
		return new JmlBuilder(startContex);
	}

	export interface ODom2JmlOptions {
		addDocType?: boolean;
		cleanupWhitespaces?: boolean;
		cleanupComments?: boolean;
		cleanupPI?: boolean;
		mergeTextNodes?: boolean;
	}

	const DEFAULT_ODom2JmlOptions = {};

	/**
	 *
	 * @param jml Jml à peupler ou null.
	 * @param addDocType Si true, joute dans le Jml le docType du document dom.
	 */
	export function dom2jml(dom: Node, jml: IJmlSet = JML.newJml(), options: ODom2JmlOptions = DEFAULT_ODom2JmlOptions): IJmlSet {
		if (!dom) return jml;
		if (options.addDocType) {
			//TODO SYSTEMID / PUBLICID.
			const doc = dom.nodeType === Node.DOCUMENT_NODE ? <Document>dom : dom.ownerDocument;
			const docType = doc.doctype ? doc.doctype.name : null;
			if (docType) jml.push({'': JML.DOCTYPE, '=': docType});
		}
		JML.domNode2jml(dom, jml, options);
		return jml;
	}

	export function dom2jmlNode(node: Node): IJmlNode {
		switch (node.nodeType) {
		case Node.TEXT_NODE :
			return node.nodeValue;
		case Node.ELEMENT_NODE :
			const elt: IJmlNode = {'': node.nodeName};
			if ((node as Element).hasAttributes()) {
				const attrs = (node as Element).attributes;
				for (let i = 0, s = attrs.length; i < s; i++) {
					const att = attrs[i];
					elt[att.name] = att.value;
				}
			}
			return elt;
		case Node.COMMENT_NODE :
			return {'': JML.COMMENT, '=': node.nodeValue};
		case Node.PROCESSING_INSTRUCTION_NODE :
			return {'': JML.PI + node.nodeName, '=': node.nodeValue};
			// case Node.DOCUMENT_NODE :
			// case Node.DOCUMENT_FRAGMENT_NODE :
		}
		return null;
	}

	/**
	 *
	 * @param node Node source à recopier.
	 * @param jml Jml à peupler (obligatoire).
	 */
	export function domNode2jml(node: Node, jml: IJmlSet, options: ODom2JmlOptions = DEFAULT_ODom2JmlOptions) {
		switch (node.nodeType) {
		case Node.TEXT_NODE :
			if (!options.cleanupWhitespaces || /\S/.test(node.nodeValue)) {
				if (options.mergeTextNodes && typeof jml[jml.length - 1] === 'string') {
					jml[jml.length - 1] += node.nodeValue;
				} else {
					jml.push(node.nodeValue);
				}
			}
			break;
		case Node.ELEMENT_NODE : {
			const elt: IJmlNode = {'': node.nodeName};
			jml.push(elt);
			if ((node as Element).hasAttributes()) {
				const attrs = (node as Element).attributes;
				for (let i = 0, s = attrs.length; i < s; i++) {
					const att = attrs[i];
					elt[att.name] = att.value;
				}
			}
			let ch = node.firstChild;
			if (ch) {
				const arr: IJmlSet = [];
				jml.push(arr);
				while (ch) {
					JML.domNode2jml(ch, arr, options);
					ch = ch.nextSibling;
				}
			}
			break;
		}
		case Node.COMMENT_NODE :
			if (!options.cleanupComments) jml.push({'': JML.COMMENT, '=': node.nodeValue});
			break;
		case Node.PROCESSING_INSTRUCTION_NODE :
			if (!options.cleanupPI) jml.push({'': JML.PI + node.nodeName, '=': node.nodeValue});
			break;
		case Node.DOCUMENT_NODE :
		case Node.DOCUMENT_FRAGMENT_NODE : {
			let ch = node.firstChild;
			while (ch) {
				JML.domNode2jml(ch, jml, options);
				ch = ch.nextSibling;
			}
			break;
		}
		}
	}


	/**
	 * @param node Node source à recopier.
	 * @param threshold Seuil approximatf avant d'arrêter de peupler (1 unité ~= 1 caractère sérialisé en json).
	 * @param jml Jml à peupler ou null.
	 * @param addDocType Si true, joute dans le Jml le docType du document dom.
	 */
	export function dom2jmlThreshold(node: Node, threshold: number, jml: IJmlSubSet = JML.newJml(), options: ODom2JmlOptions = DEFAULT_ODom2JmlOptions): IJmlSubSet {
		if (!node) return jml;
		if (options.addDocType) {
			//TODO SYSTEMID / PUBLICID.
			const doc = node.nodeType === Node.DOCUMENT_NODE ? <Document>node : node.ownerDocument;
			const docType = doc.doctype ? doc.doctype.name : null;
			if (docType) jml.push({'': JML.DOCTYPE, '=': docType});
			threshold -= 10;
		}
		JML.domNode2jmlThreshold(node, jml, threshold, options);
		return jml;
	}

	/**
	 *
	 * @param node Node source à recopier.
	 * @param jml Jml à peupler (obligatoire).
	 * @param threshold Seuil approximatf avant d'arrêter de peupler (1 unité ~= 1 caractère sérialisé en json).
	 * @return seuil diminué de la sérialisation de dom.
	 */
	export function domNode2jmlThreshold(node: Node, jml: IJmlSubSet, threshold: number, options: ODom2JmlOptions = DEFAULT_ODom2JmlOptions): number {
		switch (node.nodeType) {
		case Node.TEXT_NODE :
			if (!options.cleanupWhitespaces || /\S/.test(node.nodeValue)) {
				jml.push(node.nodeValue);
				threshold -= node.nodeValue.length;
			}
			break;
		case Node.ELEMENT_NODE : {
			const elt: IJmlNode = {'': node.nodeName};
			jml.push(elt);
			threshold -= 10;
			if ((node as Element).hasAttributes()) {
				const attrs = (node as Element).attributes;
				for (let i = 0, s = attrs.length; i < s; i++) {
					const att = attrs[i];
					elt[att.name] = att.value;
					threshold -= 10 + att.value.length;
				}
			}
			let ch = node.firstChild;
			if (ch) {
				const arr: IJmlSubSet = [];
				jml.push(arr);
				while (ch) {
					threshold = JML.domNode2jmlThreshold(ch, arr, threshold, options);
					ch = ch.nextSibling;
					if (threshold < 0 && ch) {
						while (ch) {
							arr.push(JML.computeWeightNode(ch));
							ch = ch.nextSibling;
						}
					}
				}
			}
			break;
		}
		case Node.COMMENT_NODE :
			if (!options.cleanupComments) {
				jml.push({'': JML.COMMENT, '=': node.nodeValue});
				threshold -= node.nodeValue.length;
			}
			break;
		case Node.PROCESSING_INSTRUCTION_NODE :
			if (!options.cleanupPI) {
				jml.push({'': JML.PI + node.nodeName, '=': node.nodeValue});
				threshold -= 10 + node.nodeValue.length;
			}
			break;
		case Node.DOCUMENT_NODE :
		case Node.DOCUMENT_FRAGMENT_NODE : {
			let ch = node.firstChild;
			while (ch) {
				threshold = JML.domNode2jmlThreshold(ch, jml, threshold, options);
				ch = ch.nextSibling;
				if (threshold < 0 && ch) {
					while (ch) {
						jml.push(JML.computeWeightNode(ch));
						ch = ch.nextSibling;
					}
				}
			}
			break;
		}
		}
		return threshold;
	}

	/**
	 *
	 * @param node Noeud à copier et à ajouter à to.
	 * @param deep Copie profonde ou pas.
	 * @param to Cible de type JML ou Node.
	 * @return null ou le container fils (du noeud ajouté) si c'est un élément.
	 */
	export function appendDomNode(node: Node, deep: boolean, to: IJmlSet | Node): IJmlSet | Node {
		switch (node.nodeType) {
		case Node.ELEMENT_NODE :
			if (Array.isArray(to)) {
				const elt = {'': node.nodeName} as IJmlObj;
				if ((node as Element).hasAttributes()) {
					const attrs = (node as Element).attributes;
					for (let i = 0, s = attrs.length; i < s; i++) {
						const att = attrs[i];
						elt[att.name] = att.value;
					}
				}
				const subSet = [] as IJmlSet;
				if (deep) {
					let ch = node.firstChild;
					while (ch) {
						JML.domNode2jml(ch, subSet);
						ch = ch.nextSibling;
					}
				}
				to.push(elt, subSet);
				return subSet;
			} else {
				return to.appendChild(cloneNode(node, deep));
			}
		case Node.TEXT_NODE :
			Array.isArray(to) ? to.push(node.nodeValue) : to.appendChild(cloneNode(node, false));
			return null;
		case Node.COMMENT_NODE :
			Array.isArray(to) ? to.push({'': JML.COMMENT, '=': node.nodeValue}) : to.appendChild(cloneNode(node, false));
			return null;
		case Node.PROCESSING_INSTRUCTION_NODE :
			Array.isArray(to) ? to.push({'': JML.PI + node.nodeName, '=': node.nodeValue}) : to.appendChild(cloneNode(node, false));
			return null;
		}
		return null;
	}

	export function appendText(str: string, to: IJmlSet | Node): void {
		Array.isArray(to) ? to.push(str) : to.appendChild(to.ownerDocument.createTextNode(str));
	}

	export function appendChildren(children: IJmlSubSet, parents: IJmlSubSet, parentOffset: number) {
		const parentIdx = getJmlIndexForOffset(parents, parentOffset);
		const parentch = parents[parentIdx + 1];
		if (Array.isArray(parentch)) {
			parentch.push(...children);
		} else {
			parents.splice(parentIdx + 1, 0, children);
		}
	}

	export function insertChildren(children: IJmlSubSet, insertOffset: number, parents: IJmlSubSet, parentOffset: number) {
		const parentIdx = getJmlIndexForOffset(parents, parentOffset);
		const parentch = parents[parentIdx + 1];
		if (Array.isArray(parentch)) {
			const chIdx = getJmlIndexForOffset(parentch, insertOffset);
			if (chIdx >= 0) parentch.splice(chIdx, 0, ...children);
			else parentch.push(...children);
		} else {
			parents.splice(parentIdx + 1, 0, children);
		}
	}

	/** insert ou remplace tous les fils de `to` présents dans `inSet` par ``children`. */
	export function replaceChildren(children: IJmlSubSet, to: IJmlObj, inSet: IJmlSubSet): boolean {
		for (let i = 0; i < inSet.length; i++) {
			if (inSet[i] === to) {
				if (Array.isArray(inSet[i + 1])) inSet[i + 1] = children;
				else inSet.splice(i + 1, 0, children);
				return true;
			}
		}
		return false;
	}

	export function cloneNode(node: Node, deep: boolean): Node {
		return _customCloner !== null ? _customCloner(node, deep) : node.cloneNode(deep);
	}

	/** Exécution d'un traitement avec injection d'une fonction custom de clonage d'un noeud. */
	export function doWithCustomClone(exec: () => void, cloner: (node: Node, deep: boolean) => Node) {
		try {
			_customCloner = cloner;
			exec();
		} finally {
			_customCloner = null;
		}
	}

	let _customCloner: (node: Node, deep: boolean) => Node = null;


	/**
	 * N'extrait que le type de noeud pour créer un jml container vide.
	 * @param dom Node source.
	 */
	export function domNode2jmlContainer(dom: Node): IJmlSet {
		switch (dom.nodeType) {
		case Node.TEXT_NODE :
			return [""];
		case Node.ELEMENT_NODE :
			return [{'': dom.nodeName}];
		case Node.COMMENT_NODE :
			return [{'': JML.COMMENT}];
		case Node.PROCESSING_INSTRUCTION_NODE :
			return [{'': JML.PI + dom.nodeName}];
		case Node.DOCUMENT_NODE :
		case Node.DOCUMENT_FRAGMENT_NODE :
			return [];
		}
	}

	/**
	 * Recherche un DocType avant le documentElement ou un noued texte (mais potentiellement après des comments ou PI).
	 */
	export function findDocTypeDatas(jml: IJmlSet): string {
		if (jml != null) for (let i = 0, s = jml.length; i < s; i++) {
			const node = jml[i];
			if (!JML.isJmlObj(node)) return null;
			const name = node[''];
			if (name === JML.DOCTYPE) return node['='];
			if (name !== JML.COMMENT && name !== JML.PI) return null;
		}
		return null;
	}

	/**
	 *
	 * @param jml
	 * @param domParent Si null, un nouveau document sera créé.
	 * @param domInsertBefore Si domParent est renseigné, noeud avant lequel les noeuds jml seront importés.
	 *
	 * @return Retourne domParent ou le nouveau Document créé si domParent était null.
	 */
	export function jmlToDom(jml: IJmlSet): Document;
	export function jmlToDom(jml: IJmlSet, domParent?: Document | DocumentFragment | Element, domInsertBefore?: Node): Document | DocumentFragment | Element;
	export function jmlToDom(jml: IJmlSet, domParent?: Document | DocumentFragment | Element, domInsertBefore?: Node): Document | DocumentFragment | Element {
		if (domParent == null) domParent = DOM.newDomDoc(DOM.newDomDocType(JML.findDocTypeDatas(jml)));
		const doc = domParent.nodeType === Node.DOCUMENT_NODE ? <Document>domParent : domParent.ownerDocument;

		function arrayToDom(arr: IJmlSet, parent: Document | DocumentFragment | Element, before: Node) {
			for (let i = 0, s = arr.length; i < s; i++) {
				const node = arr[i] as IJmlNode;
				if (typeof node === 'string') {
					parent.insertBefore(doc.createTextNode(<string>node), before);
					continue;
				}
				const name = node[''];
				if (name.charAt(0) === JML.COMMENT) {
					if (name === JML.COMMENT) {
						parent.insertBefore(doc.createComment(node['='] || ""), before);
					} // else jMl.DOCTYPE, on saute.
				} else if (name === JML.PI) {
					parent.insertBefore(doc.createProcessingInstruction(name.substring(1), node['=']), before);
				} else {
					const sep = name.indexOf(':');
					const ns = DOM.lookupNamespaceURI(parent, sep > 0 ? name.substring(0, sep) : ""); //ns ""  pour un elt sans prefix.
					const elt = parent.insertBefore(ns ? doc.createElementNS(ns, name) : doc.createElement(name), before);
					for (const k in node) {
						if (k != '') {
							const sep = k.indexOf(':');
							const ns = sep > 0 ? DOM.lookupNamespaceURI(parent, k.substring(0, sep)) : null; //ns null pour un attr sans prefix.
							if (ns) elt.setAttributeNS(ns, k, node[k]);
							else elt.setAttribute(k, node[k]);
						}
					}
					const ch = arr[i + 1];
					if (Array.isArray(ch)) {
						arrayToDom(ch, elt, null);
						i++;
					}
				}
			}
		}

		if (jml) arrayToDom(jml, domParent, domInsertBefore);
		return domParent;
	}

	export function jmlToElt(elt: IJmlObj, children: IJmlSet): Element {
		if (!defaultDoc) defaultDoc = DOM.newDomDoc();
		return jmlToDom(children ? [elt, children] : [elt], defaultDoc.createDocumentFragment()).firstElementChild;
	}

	let defaultDoc: Document;


	export function jmlToXml(jml: IJmlSet): string {
		const doc = DOM.newDomDoc(DOM.newDomDocType(JML.findDocTypeDatas(jml)));
		return DOM.serializer().serializeToString(JML.jmlToDom(jml, doc));
	}

	/** Retrouve le IJmlNode d'un IJmlSubSet à un offset donné. */
	export function getJmlNode(jmlSet: IJmlSubSet, offset: number): IJmlNode | number | null {
		if (jmlSet) for (let i = 0, s = jmlSet.length; i < s; i++) {
			if (offset-- === 0) return jmlSet[i] as IJmlNode | number;
			if (Array.isArray(jmlSet[i + 1])) i++; //on saute les children de ce noeud.
		}
		return null;
	}

	/** Retrouve les fils d'un noeud (ou son contenu si c'est un noeud texte) d'un IJmlSubSet à un offset donné. */
	export function getJmlChildrenOrBody(jmlSet: IJmlSubSet, offset: number): IJmlSubSet | string | null {
		if (jmlSet) for (let i = 0, s = jmlSet.length; i < s; i++) {
			const ch = jmlSet[i + 1]; //children de ce noeud (si existe)
			if (offset-- === 0) {
				if (Array.isArray(ch)) return ch as IJmlSubSet;
				const node = jmlSet[i];
				return (typeof node === 'string') ? node : null;
			}
			if (Array.isArray(ch)) i++; //on saute les children de ce noeud.
		}
		return null;
	}

	export function getJmlIndexForOffset(jmlSet: IJmlSubSet, offset: number): number {
		if (jmlSet) for (let i = 0, s = jmlSet.length; i < s; i++) {
			if (offset-- === 0) return i;
			if (Array.isArray(jmlSet[i])) i++; //on saute les children de ce noeud.
		}
		return -1;
	}


	export function findJmlEltByName(jmlSet: IJmlSet, name: string): { node: IJmlObj | null, ch: IJmlSet | null } {
		_result.node = null;
		_result.ch = null;
		if (jmlSet) {
			for (let i = 0; i < jmlSet.length; i++) {
				const n = jmlSet[i];
				const next = jmlSet[i + 1];
				if (isJmlObj(n) && n[''] === name) {
					_result.node = n;
					if (Array.isArray(next)) _result.ch = next;
					return _result;
				}
				if (Array.isArray(next)) i++;
			}
		}
		return _result;
	}

	const _result = {} as { node: IJmlObj | null, ch: IJmlSet | null };


	/** Longueur d'une séquence jml ou d'un texte. */
	export function lengthJmlOrText(jmlOrText: IJmlSubSet | string): number {
		if (jmlOrText == null) return 0;
		if (typeof jmlOrText === 'string') return jmlOrText.length;
		let count = 0;
		for (let i = 0, s = jmlOrText.length; i < s; i++) {
			const entry = jmlOrText[i];
			if (Array.isArray(entry)) continue;
			if (typeof entry == 'number') count += entry;
			else count++;
		}
		return count;
	}

	export function jmlNode2value(jmlNode: IJmlNode): string {
		if (typeof jmlNode === 'string') return jmlNode; //text
		if (jmlNode && jmlNode[''] === '!') return jmlNode['=']; //comment
		return null;
	}

	export function jmlNode2name(jmlNode: IJmlNode): string {
		if (typeof jmlNode === 'string') return "#text";
		if (jmlNode == null) return null;
		const name = jmlNode[''];
		if (name === '!') return "#comment";
		return name;
	}

	export function jmlNode2nodeType(jmlNode: IJmlNode): number {
		if (typeof jmlNode === 'string') return Node.TEXT_NODE;
		if (jmlNode == null) return null;
		const name = jmlNode[''];
		if (name === '!') return Node.COMMENT_NODE;
		return Node.ELEMENT_NODE;
	}

	export function isElt(child: IJmlNode | IJmlSet | number): child is IJmlObj {
		if (typeof child !== 'object' || Array.isArray(child)) return false;
		const name = child[''];
		return name !== '!'; //TODO PI, DOCTYPE...
	}

	export function isText(child: IJmlNode | IJmlSet | number): child is string {
		return typeof child === 'string';
	}

	export function isJmlObj(child: IJmlNode | IJmlSet | number): child is IJmlObj {
		return (typeof child === 'object') && !Array.isArray(child);
	}

	/** Evalue si un IJmlSubSet est assimilable à un IJmlSet (aucun trou matérialisé par un number) */
	export function isWhole(jml: IJmlSubSet): jml is IJmlSet {
		if (jml) for (let i = 0; i < jml.length; i++) {
			if (typeof jml[i] === 'number') return false;
			const ch = jml[i + 1];
			if (Array.isArray(ch)) {
				if (!isWhole(ch)) return false;
				i++;
			}
		}
		return true;
	}

	/**
	 * @throws Error if insertion failed.
	 */
	export function insertJmlInDom(xa: IXAddr, jmlOrText: IJmlSet | string, root: Node, forbidSplit?: boolean) {
		let ctn = XA.findDomContainer(xa, root);
		if (DOM.ASSERT) if (!ctn) throw Error(`Container not found for xa ${xa} in doc :\n ${DOM.debug(root)}`);
		const idx = XA.last(xa);
		if (typeof idx === 'string') {
			//Affectation d'un attribut
			if (DOM.ASSERT) if (ctn.nodeType !== Node.ELEMENT_NODE) throw Error(`Container not an element for xa ${xa} in doc :\n ${DOM.debug(root)}`);
			const sep = idx.indexOf(':');
			const ns = sep > 0 ? DOM.lookupNamespaceURI(ctn, idx.substring(0, sep)) : null;
			if (jmlOrText == null) {
				if (ns) (ctn as Element).removeAttributeNS(ns, idx.substring(sep + 1));
				else (ctn as Element).removeAttribute(idx);
			} else {
				if (DOM.ASSERT) if (typeof jmlOrText !== 'string') throw Error(`Attribute to set not a string for xa ${xa} in doc :\n ${DOM.debug(root)}`);
				if (ns) (ctn as Element).setAttributeNS(ns, idx, jmlOrText as string);
				else (ctn as Element).setAttribute(idx, jmlOrText as string);
			}
			return;
		}
		if (ctn instanceof Attr) {
			//insertion DANS un attribut
			ctn.value = LANG.stringInsert(ctn.value, idx, jmlOrText as string);
			return;
		}
		let nodeAfter;
		if (ctn instanceof CharacterData) {
			//text, comment, PI
			if (typeof jmlOrText === 'string') {
				//insertion du texte dans le noeud.
				if (DOM.ASSERT) if (idx < 0 || idx > ctn.data.length) throw Error(`Last index out of bounds in xa ${xa} for str '${ctn.data}' in doc :\n ${DOM.debug(root)}`);
				ctn.insertData(idx, jmlOrText);
				return;
			}
			//insertion d'un node Jml, split du noeud CharacterData.
			if (forbidSplit) throw Error(`Split CharacterData [${ctn.data}] forbidden for insertion : ${jmlOrText}`);
			if (idx === 0) {
				nodeAfter = ctn.childNodes[idx];
			} else if (idx === ctn.data.length) {
				nodeAfter = ctn.childNodes[idx].nextSibling;
			} else if (idx > 0) {
				//on doit splitter le noeud.
				if (DOM.ASSERT) if (idx < 0 || idx > ctn.data.length) throw Error(`Last index out of bounds in xa ${xa} for str '${ctn.data}' in doc :\n ${DOM.debug(root)}`);
				nodeAfter = DOM.splitDomCharacterData(ctn, idx);
			}
			ctn = ctn.parentNode;
		} else {
			if (DOM.ASSERT) if (idx < 0 || idx > ctn.childNodes.length) throw Error(`Last index out of bounds in xa ${xa} in doc :\n ${DOM.debug(root)}`);
			nodeAfter = ctn.childNodes[idx];
		}
		JML.jmlToDom(typeof jmlOrText === 'string' ? [jmlOrText] : jmlOrText, ctn as Element | Document | DocumentFragment, nodeAfter);
	}


	/**
	 * Extrait une séquence Jml d'un noeud container ou un fragment de texte d'un noeud texte, comment, PI ou attribut.
	 *
	 * @param xa Last item in xa must not be an attribute name. For extracting text in attr, xa must end by an offset : [1,'myAttr',0]
	 * @param length
	 * @param root
	 * @return Return null if xa container not found, "" or [] if last xa offset is equal or greater than container length.
	 */
	export function extractJmlOrTextFromDom(xa: IXAddr, length: number, root: Node): IJmlSet | string {
		const ctn = XA.findDomContainer(xa, root);
		if (ctn == null) return null;
		const last = XA.last(xa);
		if (typeof last === 'string') throw Error(`xAddr ${xa} must not point an attr in this function.`);
		if (ctn instanceof CharacterData) {
			//text, comment, PI
			if (DOM.ASSERT) if (last + ctn.nodeValue.length < length) throw Error(`Length ${length} out of bound from ${xa} in doc :\n ${DOM.debug(root)}`);
			return ctn.nodeValue.substr(last, length);
		} else if (ctn instanceof Attr) {
			if (DOM.ASSERT) if (last + ctn.value.length < length) throw Error(`Length ${length} out of bound from ${xa} in doc :\n ${DOM.debug(root)}`);
			return ctn.value.substr(last, length);
		}
		const jml: IJmlSet = [];
		let node = ctn.childNodes[last] as Node;
		for (let i = 0; i < length; i++) {
			if (DOM.ASSERT) if (!node) throw Error(`Length ${length} out of bound from ${xa} in doc :\n ${DOM.debug(root)}`);
			if (!node) break;
			JML.domNode2jml(node, jml, DEFAULT_ODom2JmlOptions);
			node = node.nextSibling;
		}
		return jml;
	}

	/**
	 * Calcul du poids d'un noeud non présent dans un IJmlSubSet.
	 */
	export function computeWeightNode(node: Node): number {
		if (!node) return 0;
		const nodeIt = (node.ownerDocument || node as Document).createTreeWalker(node, NodeFilter.SHOW_ALL);
		let weight = 0;
		do {
			switch (node.nodeType) {
			case Node.ELEMENT_NODE:
				weight += 50;
				break;
			case Node.TEXT_NODE:
			case Node.COMMENT_NODE:
			case Node.PROCESSING_INSTRUCTION_NODE:
				weight += node.nodeValue.length;
				break;
			}
		} while ((node = nodeIt.nextNode()) != null);
		return weight;
	}

	export function computeWeightJml(jml: IJmlSubSet): number {
		if (!jml) return 0;
		let weight = 0;
		for (let i = 0; i < jml.length; i++) {
			const node = jml[i] as IJmlNode | number;
			if (typeof node == 'number') {
				weight += node;
			} else {
				switch (jmlNode2nodeType(node)) {
				case Node.ELEMENT_NODE:
					weight += 50;
					if (Array.isArray(jml[i + 1])) weight += JML.computeWeightJml(jml[++i] as IJmlSubSet);
					break;
				case Node.TEXT_NODE:
					weight += (node as string).length;
					break;
				case Node.COMMENT_NODE:
				case Node.PROCESSING_INSTRUCTION_NODE:
					weight += (node as IJmlObj)['='].length;
					break;
				}
			}
		}
		return weight;
	}

	export function filterForbiddenXmlCharsInJml(ct: IJmlSubSet | null): IJmlSubSet | null {
		if (ct) for (let i = 0; i < ct.length; i++) {
			const n = ct[i];
			if (Array.isArray(n)) {
				filterForbiddenXmlCharsInJml(n);
			} else {
				switch (typeof n) {
				case "string":
					ct[i] = filterForbiddenXmlChars(n);
					break;
				case "object":
					for (const k in n) n[k] = filterForbiddenXmlChars(n[k]);
					break;
				}
			}
		}
		return ct;
	}

	export function filterForbiddenXmlChars(str: string): string {
		return str.replace(/[\x01-\x08\x0B-\x0C\x0E-\x1F\x7F-\x84\x86-\x9F]/g, '');
	}

	export const COMMENT = "!";
	export const DOCTYPE = "!DOCTYPE";
	export const PI = "?";
}

/**
 * Iterator sur les noeuds d'un IJmlSubSet ou IJmlSet.
 * Cet iterator donne accès aux fils de l'élément courant (currentChildren),
 * mais ne parcourt pas lui-même les noeuds fils récursivement.
 */
class JmlSubSetIterator {

	jml: IJmlSubSet;

	/** Index (logique) du noeud courant dans le set. */
	currentIdx = -1;

	/** Retourne un number si le noeud existe mais seul son poids approximatif est connu. */
	currentNode: IJmlNode | number;

	/** Fils du noeud courant. */
	currentChildren: IJmlSubSet;

	/** index physique de l'iterator dans le tableau jml. */
	protected _offset = -1;

	constructor(jml: IJmlSubSet, fromOffset = 0) {
		this.jml = jml || [];
		for (let i = 0; i < fromOffset; i++) {
			if (!this.next()) break;
		}
	}

	next(): boolean {
		if (++this._offset >= this.jml.length) return false;
		const node = this.jml[this._offset];
		this.currentIdx++;
		if (typeof node === 'number') {
			this.currentNode = node;
			this.currentChildren = null;
		} else {
			this.currentNode = node as IJmlNode;
			const ch = this.jml[this._offset + 1];
			if (Array.isArray(ch)) {
				this._offset++;
				this.currentChildren = ch;
			} else {
				this.currentChildren = null;
			}
		}
		return true;
	}
}


class JmlBuilder {

	/** stack des noeuds JML ancêtres. Enchainement de [], {}, [], {}, []... */
	protected _stack: Array<IJmlSet | IJmlObj> = [];

	protected _elt: IJmlObj;
	protected _children: IJmlSet;

	/**
	 *
	 * @param startContext @see #setStartCtx
	 */
	constructor(startContext: IJmlSet | IJmlNode) {
		//1er élément : array (= document / fragment). Couples suivants : objet (= elt) + array (= eltChildren)
		if (startContext) this.setStartCtx(startContext);
		else this._children = []; //On cree le document/fragment racine.
	}

	/**
	 * Contexte de démarrage de ce JmlBuilder.
	 *
	 * @param root Le root peut-être :
	 * - null : un nouveau contexte racine de type document/fragment est créé.
	 * - un tableau js (=document / fragment), les contenus seront ajoutés en frère des éléments déjà présents dans ce tableau.
	 * - une string (=noeud texte), un tableau js container est créé pour contenir ce noeud et les contenus seront ajoutés en frère de ce 1er noeud.
	 * - un objet js (=element, comment, pi, doctype), un tableau js container est créé pour contenir ce noeud.
	 *   Si c'est un comment, pi ou doctype, les contenus seront ajoutés en frère de ce 1er noeud.
	 *   Si c'est un élément, les contenus seront ajoutés en fils de cet élément, dans un tableau créé si children n'est pas renseigné.
	 *   children, optionnel, peut spécifier le tableau à utiliser pour constituer les fils de cet élément (qui peut contenir des premiers noeuds)
	 *
	 * @param children utilisé que si root est un élément.
	 *
	 * @return this
	 */
	setStartCtx(root: IJmlSet | IJmlNode, children?: IJmlSet): this {
		this._stack.length = 0;
		if (root == null) {
			//On cree le document/fragement racine.
			this._elt = null;
			this._children = []
		} else if (Array.isArray(root)) {
			//Fragment ou document,
			this._elt = null;
			this._children = root
		} else if (typeof root === 'string') {
			//noued texte
			this._elt = null;
			this._children = [root]
		} else {
			const name = root[''];
			if (name === JML.COMMENT || name === JML.DOCTYPE || name.charAt(0) == JML.PI) {
				this._elt = null;
				this._children = [root];
			} else {
				//Element racine
				this._elt = root;
				this._stack.push([this._elt]);
				this._children = children
			}
		}
		return this;
	}

	/**
	 * Retourne le document / fragment.
	 *
	 */
	get root(): IJmlSet {
		return this._stack[0] as IJmlSet || this._children;
	}

	/**
	 * @return this
	 */
	elt(name: string, classNames?: string, extType?: string): this {
		const newElt: IJmlObj = {};
		newElt[''] = name;
		if (classNames) newElt['class'] = classNames;
		if (extType) newElt['is'] = extType;
		this.xAddChild(newElt);
		//on entre dans cet elt, ie on remonte dans la stack le contexte courant
		if (this._elt) this._stack.push(this._elt);
		this._stack.push(this._children);
		this._elt = newElt;
		this._children = null;
		//console.log(JSON.stringify(this.root()));
		return this;
	}

	/**
	 * @return this
	 */
	att(name: string, value: string): this {
		this._elt[name] = value;
		return this;
	}

	/**
	 * @return this
	 */
	text(text: string): this {
		if (text == null) return this;
		this.xAddChild(text);
		return this;
	}

	/**
	 * @return this
	 */
	comment(comment: string): this {
		if (comment == null) return this;
		this.xAddChild({'': '!', '=': comment});
		return this;
	}

	/**
	 * @return this
	 */
	pi(name: string, value: string): this {
		if (name == null) return this;
		this.xAddChild({'': '?' + name, '=': value});
		return this;
	}

	/**
	 * @return this
	 */
	docType(value: string): this {
		if (this._elt) throw "Not at root position";
		this.xAddChild({'': '!DOCTYPE', '=': value});
		return this;
	}

	/**
	 * @return this
	 */
	up(): this {
		if (!this._elt) throw "no elt context";
		this._children = this._stack.pop() as IJmlSet;
		this._elt = this._stack.pop() as IJmlObj;
		return this;
	}

	/**
	 * Supprime tous les fils du fragment courant.
	 * @return this
	 */
	clear(): this {
		if (this._elt) this._children = null; //children de l'elt courant.
		else this._children = []; //on est à la racine.
		return this;
	}

	/**
	 * Retourne l'elt courant (Objet js)
	 */
	get currentElt(): IJmlObj {
		return this._elt;
	}

	/**
	 * Retourne les fils de l'elt courant ou à defaut le document/fragment root (tableau js)
	 */
	get currentChildren(): IJmlSet {
		return this._children || this._stack[0] as IJmlSet;
	}

	protected xAddChild(ch: IJmlNode) {
		if (!this._children) {
			this._children = [];
			(this._stack[this._stack.length - 1] as IJmlSet).push(this._children);
		}
		this._children.push(ch);
	}
}

export {IJmlObj, IJmlNode, IJmlSet, IJmlSubSet, JmlBuilder, JmlSubSetIterator};
